import { type Circle } from './Circle';
import { clamp } from './MathUtils';
import { type Rect } from './Rect';

/*
 * Overlap tests between the shapes the engine can collide.
 *
 * Pure functions over plain shapes, so the whole of collision detection is checkable without a scene,
 * an entity or a frame. Touching counts as overlapping throughout: two circles exactly "r1 + r2" apart
 * are colliding, matching "Rect.intersects".
 */

/** Whether two circles overlap. */
export function circlesIntersect(first: Circle, second: Circle): boolean {
  const deltaX = second.x - first.x;
  const deltaY = second.y - first.y;
  const reach = first.radius + second.radius;
  return deltaX * deltaX + deltaY * deltaY <= reach * reach;
}

/**
 * Whether a circle overlaps an axis aligned rectangle.
 *
 * Works by clamping the circle's center into the rectangle: the result is the nearest point on the
 * rectangle, and the circle overlaps exactly when that point is within its radius. Handles the center
 * being inside the rectangle without a separate case, because the clamp then returns the center itself.
 */
export function circleIntersectsRect(circle: Circle, rect: Rect): boolean {
  const nearestX = clamp(circle.x, rect.left, rect.right);
  const nearestY = clamp(circle.y, rect.bottom, rect.top);
  const deltaX = circle.x - nearestX;
  const deltaY = circle.y - nearestY;
  return deltaX * deltaX + deltaY * deltaY <= circle.radius * circle.radius;
}
